/**
 * Toast Notification Service
 *
 * Provides a unified way to display success, error, warning, and info notifications
 * across the entire application.
 *
 * Usage:
 *   import { toast } from '../services/toast.js';
 *
 *   toast.success('Operation completed successfully');
 *   toast.error('Something went wrong');
 *   toast.warning('Please review before continuing');
 *   toast.info('New update available');
 *
 * Options:
 *   toast.success('Message', { duration: 5000, dismissible: true });
 */

import { t } from '../i18n/translations.js';

// Toast types with their configurations
const TOAST_TYPES = {
  success: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>`,
    className: 'toast--success',
    defaultDuration: 5000,
  },
  error: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>`,
    className: 'toast--error',
    defaultDuration: 0, // Errors persist until dismissed
  },
  warning: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>`,
    className: 'toast--warning',
    defaultDuration: 7000,
  },
  info: {
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>`,
    className: 'toast--info',
    defaultDuration: 5000,
  },
};

// Active toasts
let toasts = [];
let toastId = 0;
let container = null;
let stylesInjected = false;

/**
 * Inject toast styles into the document
 */
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.id = 'toast-styles';
  style.textContent = `
    .toast-container {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 12px;
      pointer-events: none;
      max-width: 420px;
      width: 100%;
    }

    .toast {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 16px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1);
      pointer-events: auto;
      animation: toast-slide-in 0.3s ease-out;
      border-left: 4px solid;
      min-width: 300px;
    }

    .toast.toast--removing {
      animation: toast-slide-out 0.2s ease-in forwards;
    }

    @keyframes toast-slide-in {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes toast-slide-out {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }

    .toast__icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
    }

    .toast__icon svg {
      width: 100%;
      height: 100%;
    }

    .toast__content {
      flex: 1;
      min-width: 0;
    }

    .toast__title {
      font-weight: 600;
      font-size: 14px;
      margin: 0 0 2px 0;
      color: #1a1a1a;
    }

    .toast__message {
      font-size: 13px;
      color: #4a5568;
      margin: 0;
      line-height: 1.4;
      word-wrap: break-word;
    }

    .toast__close {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #9ca3af;
      border-radius: 4px;
      transition: background 0.15s, color 0.15s;
      margin: -4px -4px -4px 0;
    }

    .toast__close:hover {
      background: #f3f4f6;
      color: #374151;
    }

    .toast__close svg {
      width: 16px;
      height: 16px;
    }

    .toast__progress {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 0 0 4px 4px;
      overflow: hidden;
    }

    .toast__progress-bar {
      height: 100%;
      background: currentColor;
      opacity: 0.3;
      transition: width linear;
    }

    /* Toast types */
    .toast--success {
      border-left-color: #10b981;
    }
    .toast--success .toast__icon {
      color: #10b981;
    }
    .toast--success .toast__title {
      color: #065f46;
    }

    .toast--error {
      border-left-color: #ef4444;
    }
    .toast--error .toast__icon {
      color: #ef4444;
    }
    .toast--error .toast__title {
      color: #991b1b;
    }

    .toast--warning {
      border-left-color: #f59e0b;
    }
    .toast--warning .toast__icon {
      color: #f59e0b;
    }
    .toast--warning .toast__title {
      color: #92400e;
    }

    .toast--info {
      border-left-color: #3b82f6;
    }
    .toast--info .toast__icon {
      color: #3b82f6;
    }
    .toast--info .toast__title {
      color: #1e40af;
    }

    /* Responsive */
    @media (max-width: 480px) {
      .toast-container {
        left: 12px;
        right: 12px;
        top: 12px;
        max-width: none;
      }

      .toast {
        min-width: 0;
      }
    }

    /* Dark mode support */
    @media (prefers-color-scheme: dark) {
      .toast {
        background: #1f2937;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      }
      .toast__title {
        color: #f9fafb;
      }
      .toast__message {
        color: #d1d5db;
      }
      .toast__close {
        color: #6b7280;
      }
      .toast__close:hover {
        background: #374151;
        color: #e5e7eb;
      }
      .toast--success .toast__title { color: #6ee7b7; }
      .toast--error .toast__title { color: #fca5a5; }
      .toast--warning .toast__title { color: #fcd34d; }
      .toast--info .toast__title { color: #93c5fd; }
    }
  `;

  document.head.appendChild(style);
}

/**
 * Create or get the toast container
 */
function getContainer() {
  if (container && document.body.contains(container)) {
    return container;
  }

  container = document.createElement('div');
  container.className = 'toast-container';
  container.setAttribute('role', 'alert');
  container.setAttribute('aria-live', 'polite');
  document.body.appendChild(container);

  return container;
}

/**
 * Remove a toast by ID
 */
function removeToast(id) {
  const toastEl = document.querySelector(`[data-toast-id="${id}"]`);
  if (toastEl) {
    toastEl.classList.add('toast--removing');
    setTimeout(() => {
      toastEl.remove();
      toasts = toasts.filter(t => t.id !== id);

      // Remove container if empty
      if (toasts.length === 0 && container) {
        container.remove();
        container = null;
      }
    }, 200);
  }
}

/**
 * Show a toast notification
 * @param {string} type - Toast type: success, error, warning, info
 * @param {string} message - Message to display
 * @param {Object} options - Optional configuration
 * @returns {number} Toast ID for manual dismissal
 */
function showToast(type, message, options = {}) {
  injectStyles();
  const containerEl = getContainer();

  const config = TOAST_TYPES[type] || TOAST_TYPES.info;
  const id = ++toastId;
  const duration = options.duration !== undefined ? options.duration : config.defaultDuration;
  const title = options.title || getDefaultTitle(type);
  const dismissible = options.dismissible !== false;

  // Create toast element
  const toastEl = document.createElement('div');
  toastEl.className = `toast ${config.className}`;
  toastEl.dataset.toastId = id;
  toastEl.innerHTML = `
    <div class="toast__icon">${config.icon}</div>
    <div class="toast__content">
      ${title ? `<p class="toast__title">${escapeHtml(title)}</p>` : ''}
      <p class="toast__message">${escapeHtml(message)}</p>
    </div>
    ${dismissible ? `
      <button class="toast__close" aria-label="${t('common.close') || 'Close'}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    ` : ''}
    ${duration > 0 ? `
      <div class="toast__progress">
        <div class="toast__progress-bar" style="width: 100%; transition-duration: ${duration}ms;"></div>
      </div>
    ` : ''}
  `;

  // Add close button handler
  if (dismissible) {
    const closeBtn = toastEl.querySelector('.toast__close');
    closeBtn?.addEventListener('click', () => removeToast(id));
  }

  // Add to container
  containerEl.appendChild(toastEl);

  // Start progress bar animation
  if (duration > 0) {
    const progressBar = toastEl.querySelector('.toast__progress-bar');
    if (progressBar) {
      // Trigger reflow to start animation
      progressBar.offsetHeight;
      progressBar.style.width = '0%';
    }
  }

  // Store toast reference
  const toastData = { id, type, message, element: toastEl };
  toasts.push(toastData);

  // Auto-dismiss if duration > 0
  if (duration > 0) {
    toastData.timeoutId = setTimeout(() => removeToast(id), duration);
  }

  return id;
}

/**
 * Get default title based on type
 */
function getDefaultTitle(type) {
  const titles = {
    success: t('toast.success') || 'Success',
    error: t('toast.error') || 'Error',
    warning: t('toast.warning') || 'Warning',
    info: t('toast.info') || 'Info',
  };
  return titles[type] || '';
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Toast API
 */
export const toast = {
  /**
   * Show a success toast
   * @param {string} message - Message to display
   * @param {Object} options - { title, duration, dismissible }
   */
  success(message, options = {}) {
    return showToast('success', message, options);
  },

  /**
   * Show an error toast (persists until dismissed by default)
   * @param {string} message - Error message
   * @param {Object} options - { title, duration, dismissible }
   */
  error(message, options = {}) {
    return showToast('error', message, options);
  },

  /**
   * Show a warning toast
   * @param {string} message - Warning message
   * @param {Object} options - { title, duration, dismissible }
   */
  warning(message, options = {}) {
    return showToast('warning', message, options);
  },

  /**
   * Show an info toast
   * @param {string} message - Info message
   * @param {Object} options - { title, duration, dismissible }
   */
  info(message, options = {}) {
    return showToast('info', message, options);
  },

  /**
   * Dismiss a toast by ID
   * @param {number} id - Toast ID returned from show methods
   */
  dismiss(id) {
    removeToast(id);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    toasts.forEach(t => removeToast(t.id));
  },

  /**
   * Show a promise-based toast (loading -> success/error)
   * @param {Promise} promise - Promise to track
   * @param {Object} messages - { loading, success, error }
   */
  async promise(promise, messages = {}) {
    const loadingId = showToast('info', messages.loading || 'Loading...', {
      duration: 0,
      dismissible: false,
    });

    try {
      const result = await promise;
      removeToast(loadingId);
      showToast('success', messages.success || 'Completed successfully');
      return result;
    } catch (error) {
      removeToast(loadingId);
      showToast('error', messages.error || error.message || 'An error occurred');
      throw error;
    }
  },
};

// Also export individual functions for convenience
export const { success, error, warning, info, dismiss, dismissAll } = toast;

export default toast;
